import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Menu, X, Clock, Truck, ShieldCheck, Lock } from "lucide-react";
import { useEmailCapture } from "../components/EmailCaptureProvider";

export const Header = () => {
  const { openModal } = useEmailCapture();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 47, seconds: 33 });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header role="banner">
      {/* Urgency Banner */}
      <div onClick={openModal} className="cursor-pointer" role="alert" aria-label="Limited time offer">
        <div className="bg-gradient-to-r from-[#C41E3A] to-[#FF4D4D] text-white py-2 px-4 text-center text-sm font-medium hover:opacity-90 transition-opacity">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Clock size={16} className="animate-pulse" aria-hidden="true" />
            <span>LIMITED TIME: </span>
            <span className="font-bold">Up to 80% OFF</span>
            <span>ends in</span>
            <time className="bg-black/20 px-2 py-0.5 rounded font-mono font-bold">
              {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
            </time>
          </div>
        </div>
      </div>

      {/* Trust Bar - Large & Bold */}
      <div className="bg-[#E8F5E9] py-3 border-b border-[#C8E6C9]" aria-label="Trust indicators">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2">
              <Truck size={24} className="text-[#2E7D32]" aria-hidden="true" />
              <span className="text-lg md:text-xl font-black text-[#2E7D32]">Free Shipping</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={24} className="text-[#2E7D32]" aria-hidden="true" />
              <span className="text-lg md:text-xl font-black text-[#2E7D32]">180 Day Money Back Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock size={24} className="text-[#2E7D32]" aria-hidden="true" />
              <span className="text-lg md:text-xl font-black text-[#2E7D32]">Secure Checkout</span>
            </div>
          </div>
        </div>
      </div>

      <nav
        data-testid="header"
        aria-label="Main navigation"
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-lg py-2" : "bg-white/95 backdrop-blur-sm py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Empty space */}
          <div className="w-32"></div>

          <div className="hidden md:flex items-center gap-6">
            <button onClick={openModal} className="text-[#4A5043] hover:text-[#FF6B00] font-medium transition-colors text-sm">
              How It Works
            </button>
            <button onClick={openModal} className="text-[#4A5043] hover:text-[#FF6B00] font-medium transition-colors text-sm">
              Benefits
            </button>
            <button onClick={openModal} className="text-[#4A5043] hover:text-[#FF6B00] font-medium transition-colors text-sm">
              Reviews
            </button>
            <button onClick={openModal} className="text-[#4A5043] hover:text-[#FF6B00] font-medium transition-colors text-sm">
              FAQ
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button 
              onClick={openModal}
              data-testid="header-cta"
              className="hidden md:inline-flex bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] hover:from-[#E65C00] hover:to-[#FF6B00] text-white font-bold rounded-full px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse"
            >
              Order Now - Save 80%
            </Button>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 p-6 space-y-4 shadow-xl">
            <button onClick={() => { openModal(); setMobileMenuOpen(false); }} className="block w-full text-left text-lg text-[#4A5043] hover:text-[#FF6B00]">How It Works</button>
            <button onClick={() => { openModal(); setMobileMenuOpen(false); }} className="block w-full text-left text-lg text-[#4A5043] hover:text-[#FF6B00]">Benefits</button>
            <button onClick={() => { openModal(); setMobileMenuOpen(false); }} className="block w-full text-left text-lg text-[#4A5043] hover:text-[#FF6B00]">Reviews</button>
            <button onClick={() => { openModal(); setMobileMenuOpen(false); }} className="block w-full text-left text-lg text-[#4A5043] hover:text-[#FF6B00]">FAQ</button>
            <Button 
              onClick={() => { openModal(); setMobileMenuOpen(false); }}
              className="w-full bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] text-white font-bold rounded-full py-3"
            >
              Order Now - Save 80%
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
